<script setup lang="ts" generic="T extends CategorySearchResultTypes">
import type { Category } from "mws-ts-v2-sdk";

import type { CategorySearchResultTypes } from "~/types/Search";

defineProps<{
  categoryResults: Category[];
  categoryType: T;
  hideBorder?: boolean;
}>();

type Emits = {
  (e: "search-item-clicked", category: Category, type: T): void;
};

defineEmits<Emits>();

const localePath = useLocalePath();
const route = useRoute();
const { isMobile } = useScreenSize();

const tabFilterApplied = computed(() => Boolean(route.params.searchFilter));
</script>

<template>
  <div class="block-container">
    <div
      class="browser-viewport-span md:site-viewport-container flex flex-col md:border-b md:gap-4 pt-6 md:py-6 uppercase"
      :class="{
        'category-search-results__container--hide-border': hideBorder,
      }"
    >
      <SearchResultPageSubHeader
        v-if="!tabFilterApplied"
        :type="categoryType as T"
      />
      <div class="category-search-results__grid">
        <NuxtLink
          v-for="item in categoryResults"
          :key="item.id"
          :to="localePath(`/category/${item.slug}`)"
          class="category-search-results__block interaction-white"
          @click="$emit('search-item-clicked', item, categoryType as T)"
        >
          <div class="category-search-results__item">
            <NuxtImg
              v-if="item.logoUrl"
              provider="imgix"
              preset="logoMedium"
              :alt="`${item.name} logo`"
              :src="item.logoUrl"
              class="category-search-results__item--logo"
            />
            <BaseTeamLogoIcon
              v-else
              class="category-search-results__item--logo"
            />
            <p class="font-semibold md:font-normal leading-none">
              {{ item.name }}
            </p>
          </div>
          <BaseIcon
            v-if="isMobile"
            name="chevronRight"
            class="text-platform-text-1"
            :size="20"
          />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.category-search-results {
  &__container {
    &--hide-border {
      @apply md:border-b-0;
    }
  }

  &__grid {
    @apply md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-2;
  }

  &__block {
    @apply flex flex-row items-center justify-between;
    @apply border-b md:border p-4 md:p-5;
  }

  &__event-item {
    @apply flex w-full justify-between gap-4 items-center;
    @apply text-sm;
  }

  &__item {
    @apply flex w-full justify-start gap-4 items-center;
    @apply text-sm;

    &--logo {
      @apply w-6 h-6 object-contain;
    }
  }
}
</style>
